import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, Instagram } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useStore } from "@/lib/store";
import { INSTAGRAM_URL } from "@/lib/api";

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex rounded-md border border-white/15 p-0.5 text-xs font-extrabold ${className}`} data-testid="lang-toggle">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`rounded px-2.5 py-1 uppercase transition-colors ${lang === l ? "bg-gold text-ink" : "text-slate-300 hover:text-white"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const SiteHeader = () => {
  const { t } = useLang();
  const { items } = useStore();
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/order", label: t("nav.order"), id: "nav-order" },
    { to: "/#types", label: t("nav.types"), id: "nav-types" },
    { to: "/#pricing", label: t("nav.pricing"), id: "nav-pricing" },
    { to: "/track", label: t("nav.track"), id: "nav-order-lookup" },
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-5 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/brand/logo-sm.png" alt="MC Illusions Prints" className="h-11 w-auto" />
          <span className="font-display text-lg leading-none text-white">
            MC <span className="text-gold">Illusions</span>
            <span className="block text-[10px] font-sans font-extrabold tracking-[0.35em] text-ruby">PRINTS</span>
          </span>
        </Link>
        <nav className="ml-6 hidden items-center gap-6 md:flex">
          {links.map((l) => (
            <NavLink key={l.id} to={l.to} data-testid={l.id} className="text-sm font-bold text-slate-300 transition-colors hover:text-gold">
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hidden text-slate-300 hover:text-gold sm:block" data-testid="nav-instagram" aria-label="Instagram">
            <Instagram className="h-5 w-5" />
          </a>
          <LangToggle />
          <Link to="/cart" className="relative rounded-md border border-white/15 p-2 text-white transition-colors hover:border-gold" data-testid="nav-cart-btn" aria-label={t("nav.cart")}>
            <ShoppingBag className="h-5 w-5" />
            {items.length > 0 && (
              <span className="absolute -right-2 -top-2 grid h-5 min-w-5 place-items-center rounded-full bg-ruby px-1 text-[11px] font-extrabold" data-testid="nav-cart-count">
                {items.length}
              </span>
            )}
          </Link>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="flex flex-col gap-1 border-t border-white/10 px-5 py-3 md:hidden">
          {links.map((l) => (
            <Link key={l.id} to={l.to} onClick={() => setOpen(false)} className="py-2 font-bold text-slate-200" data-testid={`${l.id}-mobile`}>
              {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};
